import type { Translator } from '@/i18n';
import { Icon } from './Icon';

interface Props {
  t: Translator['t'];
  micEnabled: boolean;
  videoEnabled: boolean;
  screenSharing: boolean;
  screenShareSupported: boolean;
  advancedVisible: boolean;
  onToggleMic: () => void;
  onToggleVideo: () => void;
  onToggleScreenShare: () => void;
  onToggleAdvanced: () => void;
  onHangUp: () => void;
}

/**
 * Панель дій дзвінка. Кнопки 56×56 з радіусом 16 і кольорами Google Meet —
 * ті самі значення, що в `ModernButton` мобільного клієнта.
 *
 * Тут немає перемикання фронт/тил камери, вибору гучномовця й «картинки в
 * картинці»: браузер їх не виконує, і у Flutter вони так само сховані під
 * `!kIsWeb`.
 */
export function CallControls({
  t,
  micEnabled,
  videoEnabled,
  screenSharing,
  screenShareSupported,
  advancedVisible,
  onToggleMic,
  onToggleVideo,
  onToggleScreenShare,
  onToggleAdvanced,
  onHangUp,
}: Props): React.JSX.Element {
  return (
    <div className={advancedVisible ? 'controls controls--raised' : 'controls'}>
      <button
        type="button"
        className={micEnabled ? 'control control--on' : 'control'}
        aria-label={t('mic')}
        aria-pressed={micEnabled}
        onClick={onToggleMic}
      >
        <Icon name={micEnabled ? 'mic' : 'mic_off'} size={26} />
      </button>

      <button
        type="button"
        className={videoEnabled ? 'control control--on' : 'control'}
        aria-label={t('video')}
        aria-pressed={videoEnabled}
        onClick={onToggleVideo}
      >
        <Icon name={videoEnabled ? 'videocam' : 'videocam_off'} size={26} />
      </button>

      {screenShareSupported && (
        <button
          type="button"
          className={screenSharing ? 'control control--share-on' : 'control'}
          aria-label={t('share')}
          aria-pressed={screenSharing}
          onClick={onToggleScreenShare}
        >
          <Icon name={screenSharing ? 'stop_screen_share' : 'screen_share'} size={26} />
        </button>
      )}

      <button type="button" className="control control--danger" aria-label={t('ok')} onClick={onHangUp}>
        <Icon name="call_end" size={26} />
      </button>

      <button
        type="button"
        className="control"
        aria-label={t('mySettings')}
        aria-expanded={advancedVisible}
        onClick={onToggleAdvanced}
      >
        <Icon
          name={advancedVisible ? 'keyboard_double_arrow_down' : 'keyboard_double_arrow_up'}
          size={26}
        />
      </button>
    </div>
  );
}
